/**
 * EMZI NEXUS V6.3 - Toast Notification Utility
 * Provides consistent, accessible toast notifications across the app
 */

export const ToastType = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

/**
 * Show a toast notification
 * @param {string} message - The message to display
 * @param {string} type - Type of toast (success, error, warning, info)
 * @param {number} duration - Duration in ms (default: 4000)
 */
export const showToast = (message, type = ToastType.INFO, duration = 4000) => {
  // Create toast container if it doesn't exist
  let container = document.getElementById('emzi-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'emzi-toast-container';
    container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 99999;
      display: flex;
      flex-direction: column;
      gap: 12px;
      max-width: 400px;
    `;
    document.body.appendChild(container);
  }

  // Create toast element
  const toast = document.createElement('div');
  toast.style.cssText = `
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    animation: slideInRight 0.3s ease-out;
    cursor: pointer;
    transition: opacity 0.2s;
    ${getToastStyles(type)}
  `;
  
  // Add icon and message
  const icon = getToastIcon(type);
  toast.innerHTML = `
    <div style="display: flex; align-items: center; gap: 12px;">
      <span style="font-size: 18px;">${icon}</span>
      <span style="flex: 1;">${message}</span>
      <button style="
        background: transparent;
        border: none;
        color: inherit;
        font-size: 18px;
        cursor: pointer;
        opacity: 0.7;
        padding: 0;
        margin-left: 8px;
      " onclick="this.parentElement.parentElement.remove()">×</button>
    </div>
  `;

  // Add animation keyframes if not already added
  if (!document.getElementById('toast-animations')) {
    const style = document.createElement('style');
    style.id = 'toast-animations';
    style.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOutRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(400px);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // Add to container
  container.appendChild(toast);

  // Click to dismiss
  toast.addEventListener('click', () => {
    toast.style.animation = 'slideOutRight 0.2s ease-in';
    setTimeout(() => toast.remove(), 200);
  });

  // Auto dismiss
  setTimeout(() => {
    if (toast.parentElement) {
      toast.style.animation = 'slideOutRight 0.2s ease-in';
      setTimeout(() => toast.remove(), 200);
    }
  }, duration);
};

/**
 * Get toast styles based on type
 */
const getToastStyles = (type) => {
  switch (type) {
    case ToastType.SUCCESS:
      return 'background: #10b981; color: white;';
    case ToastType.ERROR:
      return 'background: #ef4444; color: white;';
    case ToastType.WARNING:
      return 'background: #f59e0b; color: white;';
    case ToastType.INFO:
    default:
      return 'background: #3b82f6; color: white;';
  }
};

/**
 * Get toast icon based on type
 */
const getToastIcon = (type) => {
  switch (type) {
    case ToastType.SUCCESS:
      return '✓';
    case ToastType.ERROR:
      return '✕';
    case ToastType.WARNING:
      return '⚠';
    case ToastType.INFO:
    default:
      return 'ℹ';
  }
};

// Convenience methods
export const toast = {
  success: (message, duration) => showToast(message, ToastType.SUCCESS, duration),
  error: (message, duration) => showToast(message, ToastType.ERROR, duration),
  warning: (message, duration) => showToast(message, ToastType.WARNING, duration),
  info: (message, duration) => showToast(message, ToastType.INFO, duration)
};

export default toast;
